"use client";

import { Flex } from "@radix-ui/themes";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { PropsWithChildren, Suspense, useEffect, useState } from "react";
import { AuthProvider } from "@/auth-types";
import ActionContainer from "@/components/ActionContainer";
import NoAccessToTNDialog from "@/components/NoAccessToTNDialog";
import RequestTNComponent from "@/components/RequestTNComponent";
import SignInDialog from "@/components/SignInDialog";
import { useAccountQuery, useAccountsQuery } from "@/hooks/useAccountsQuery";
import { useSetTimeout } from "@/hooks/useSetTimeout";
import useTNFromRoute from "@/hooks/useTNFromRoute";
import useUbidFromRoute from "@/hooks/useUbidFromRoute";
import useVFCCSessionStore, { VerificationState } from "@/stores/VFCCSessionStore";

const refreshSuggestionMs = 10000;
const checkUbTn = process.env.NEXT_PUBLIC_AUTH_UB_TN?.toLocaleLowerCase() === "true";

export function AuthSessionValidator({ children }: PropsWithChildren) {
    // <Suspense> is NextJS requirement: https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
    return <Suspense>{!checkUbTn ? children : <AuthSessionValidatorCore>{children}</AuthSessionValidatorCore>}</Suspense>;
}

export function AuthSessionValidatorCore({ children }: PropsWithChildren) {
    const { status, data: session } = useSession();
    const { tn } = useTNFromRoute();
    const { ubid, setUbid } = useUbidFromRoute();
    const pathname: string = usePathname();
    const { vfccSession, setVFCCSession, getVFCCSession } = useVFCCSessionStore();
    const accountsQueryResult = useAccountsQuery(session?.user.provider, vfccSession);
    const accountQueryResult = useAccountQuery(session?.user.provider, ubid, vfccSession);

    useEffect(() => {
        useVFCCSessionStore.persist.rehydrate();
    }, []);

    useEffect(() => {
        const currentSession = getVFCCSession();
        if (accountsQueryResult.data) {
            for (const account of accountsQueryResult.data.accounts) {
                const verifiedTN = account?.tns?.find((tnInfo) => tnInfo.tn === tn);
                if (verifiedTN) {
                    setUbid(account.ubid);
                    setVFCCSession({
                        state: VerificationState.VERIFIED,
                        message: "Verified",
                        ubid: account.ubid,
                        tnInfo: verifiedTN,
                        tnInfoList: account.tns,
                    });

                    return;
                }
            }

            setVFCCSession({
                ...currentSession,
                tnInfo: undefined,
                state: VerificationState.UNVERIFIED,
                message: "Telephone Number not found in account",
            });
        } else if (accountQueryResult.data) {
            const verifiedTN = accountQueryResult.data?.tns?.find((tnInfo) => tnInfo.tn === tn);
            setVFCCSession({
                state: verifiedTN ? VerificationState.VERIFIED : VerificationState.UNVERIFIED,
                message: verifiedTN ? "Verified" : "Telephone Number not found in account",
                tnInfo: verifiedTN,
                ubid: accountQueryResult.data.ubid,
                tnInfoList: accountQueryResult.data.tns,
            });
        } else if (tn.length) {
            const verifiedTN = currentSession.tnInfoList?.find((tnInfo) => tnInfo.tn === tn);
            setVFCCSession({
                ...currentSession,
                state: verifiedTN ? VerificationState.VERIFIED : VerificationState.UNVERIFIED,
                message: verifiedTN ? "Verified" : "Telephone Number not found in account",
                tnInfo: verifiedTN,
            });
        }
    }, [tn, accountsQueryResult.data, accountQueryResult.data, getVFCCSession, setVFCCSession, setUbid]);

    const vfccSessionState = {
        isValid: false,
        title: "",
        body: "",
        action: (<></>) as React.ReactNode,
    };

    if (pathname.startsWith("/auth")) {
        vfccSessionState.isValid = true; // Skip verification for auth pages
    } else if (!status || status === "loading") {
        return <CheckingProfileMessage msg="Checking session profile..." />;
    } else if (status === "unauthenticated" || !session) {
        vfccSessionState.action = <SignInDialog open={true} />;
    } else if (!session.user) {
        vfccSessionState.title = "Unable to retrieve session details.";
        vfccSessionState.body = "Please try refreshing the page or signing out and signing back in.";
    } else if (pathname.startsWith("/account-settings")) {
        vfccSessionState.isValid = true; // Skip verification for settings
    } else if (
        vfccSession.state === VerificationState.UNVERIFIED &&
        (accountsQueryResult.isFetching || accountQueryResult.isFetching || accountsQueryResult.isLoading || accountQueryResult.isLoading)
    ) {
        vfccSessionState.body = "Verifying telephone number inventory...";
    } else if (!tn && vfccSession.state !== VerificationState.ERROR) {
        vfccSessionState.action = <RequestTNComponent accountsQueryResult={accountsQueryResult} accountQueryResult={accountQueryResult}></RequestTNComponent>;
    } else if (session.user.provider === AuthProvider.HaloB && vfccSession.state === VerificationState.UNVERIFIED) {
        vfccSessionState.action = <NoAccessToTNDialog open={true} />;
    } else {
        vfccSessionState.isValid = true;
    }

    return (
        <>
            {vfccSessionState.isValid ? (
                children
            ) : (
                <Flex direction="column" align="stretch" p="8" gap="2" height="100%">
                    <ActionContainer>{vfccSessionState.action}</ActionContainer>
                </Flex>
            )}
        </>
    );
}

function CheckingProfileMessage({ msg }: { msg?: string | null }) {
    const [timeoutPassed, setTimeoutPassed] = useState(false);
    useSetTimeout(() => setTimeoutPassed(true), refreshSuggestionMs);

    //TODO: add progress bar, skeleton, or spinner
    return (
        <>
            {!!msg && <p>{msg}</p>}
            {timeoutPassed && <p>Please, try refreshing the page.</p>}
        </>
    );
}
